import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { CheckCircle2, MailCheck, AlertTriangle } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | ok | error
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token) { setStatus("error"); setError("Missing verification token"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setEmail(data.email || "");
        setStatus("ok");
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        setStatus("error");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F5F4F0]">
      <div className="w-full max-w-md card-soft p-8 md:p-10 fade-in-up text-center" data-testid="verify-email-page">
        {status === "checking" && (
          <>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#5C7A4F]/10 text-[#5C7A4F] mb-4">
              <MailCheck size={22} />
            </div>
            <h2 className="font-heading text-3xl font-semibold text-[#2C342A]">Verifying your email…</h2>
            <p className="text-[#707A6E] mt-2">One moment.</p>
          </>
        )}
        {status === "ok" && (
          <div data-testid="verify-email-success">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-700 mb-4">
              <CheckCircle2 size={28} />
            </div>
            <h2 className="font-heading text-3xl font-semibold text-[#2C342A]">You're verified</h2>
            <p className="text-[#707A6E] mt-2">
              {email ? <><b>{email}</b> is confirmed. </> : ""}You can now receive invites, cash-out alerts and the weekly recap.
            </p>
            <Link to="/" className="mt-6 inline-block rounded-xl bg-[#5C7A4F] text-white px-6 py-3 font-medium hover:bg-[#4B6640]" data-testid="verify-goto-dashboard">
              Go to dashboard
            </Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-email-error">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#C06E52]/10 text-[#C06E52] mb-4">
              <AlertTriangle size={28} />
            </div>
            <h2 className="font-heading text-3xl font-semibold text-[#2C342A]">Something's off</h2>
            <p className="text-[#707A6E] mt-2">{error || "Invalid or expired verification link."}</p>
            <p className="text-sm text-[#707A6E] mt-4">
              Sign in and tap "Resend verification" on the dashboard.
            </p>
            <Link to="/login" className="mt-6 inline-block rounded-xl border border-[#E4E2DC] bg-white px-6 py-3 font-medium hover:bg-[#F0EFEB]">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
